// src/components/Application/Application.tsx
import React11 from "react";
import { Spinner as Spinner2 } from "@economic/taco";

// src/components/ErrorBoundary.tsx
import React7 from "react";

// src/tracing/log.ts
import { datadogRum } from "@datadog/browser-rum";
var error = (error2, context) => {
  datadogRum.addError(error2, context);
};
var log_default = {
  action: datadogRum.addAction,
  error,
  timing: datadogRum.addTiming,
  customVital: {
    addDurationVital: datadogRum.addDurationVital,
    startDurationVital: datadogRum.startDurationVital,
    stopDurationVital: datadogRum.stopDurationVital
  }
};

// src/components/UnexpectedError.tsx
import React6 from "react";
import { Button } from "@economic/taco";

// src/components/Layout.tsx
import React5 from "react";
import { Navigation as TacoNavigation } from "@economic/taco";
import { useLocation } from "react-router-dom";

// src/routing/Authorise.tsx
import React4 from "react";
import { useHref } from "react-router-dom";

// src/routing/useRouteAuthorisation.ts
import React3 from "react";

// src/components/Application/useApplicationContext.tsx
import React2 from "react";

// src/components/Application/ApplicationContext.tsx
import React from "react";
var ApplicationContext = React.createContext(null);

// src/components/Application/useApplicationContext.tsx
function useApplicationContext() {
  const context = React2.useContext(ApplicationContext);
  if (!context) {
    throw new Error("useApplicationContext must be used within an ApplicationProvider");
  }
  return context;
}

// src/routing/utils/matchRoute.ts
function normalizePath(path) {
  if (path === "/" || path === "") return "/";
  return path.replace(/\/+$/, "");
}
function matchSplatRoute(path, route) {
  const routeRegex = new RegExp(`^${route}(?:\\/|$)`);
  const match = path.match(routeRegex);
  return !!match && path !== route;
}
function isMatchingSplatRoute(normalizedPath, route) {
  const isSplatRoute = route.endsWith("/*");
  if (!isSplatRoute) return false;
  const normalizedRoute = route.slice(0, -2);
  return matchSplatRoute(normalizedPath, normalizedRoute);
}
function matchRoute(routes, pathname) {
  const normalizedPath = normalizePath(pathname);
  for (const route of routes) {
    if (isMatchingSplatRoute(normalizedPath, route) || normalizedPath === route) {
      return route;
    }
  }
  return null;
}

// src/routing/useRouteAuthorisation.ts
var useRouteAuthorisation = (route) => {
  const { configuration } = useApplicationContext();
  const authorisedRoute = React3.useMemo(
    () => matchRoute(Object.keys(configuration.routes), route),
    [configuration.routes, route]
  );
  const legacyUrl = authorisedRoute ? configuration.routes[authorisedRoute] : null;
  return [!!authorisedRoute, legacyUrl];
};

// src/routing/Authorise.tsx
var AuthoriseRoute = (props) => {
  const { children, path } = props;
  const [authorised] = useRouteAuthorisation(path);
  if (authorised) {
    return children;
  }
  return null;
};
var AuthorisePath = (props) => {
  const { path, ...otherProps } = props;
  const route = useHref(path);
  return /* @__PURE__ */ React4.createElement(AuthoriseRoute, { ...otherProps, path: route });
};
var Authorise = (props) => {
  if (props.path.startsWith("/")) {
    return /* @__PURE__ */ React4.createElement(AuthoriseRoute, { ...props });
  }
  return /* @__PURE__ */ React4.createElement(AuthorisePath, { ...props });
};

// src/components/Layout.tsx
var MenuGroup = React5.forwardRef((props, ref) => {
  const { expanded, fixed, path, ...otherProps } = props;
  const location = useLocation();
  let onClick;
  let isExpanded = expanded;
  if (path) {
    let lastLocalStorageValue = null;
    if (window.localStorage && !fixed) {
      const storageKey = `menu.${path}`;
      lastLocalStorageValue = window.localStorage.getItem(storageKey);
      onClick = (_, isExpanded2) => {
        window.localStorage.setItem(storageKey, isExpanded2 ? "true" : "false");
      };
    }
    const isActiveBaseRoute = location.pathname.startsWith(path);
    isExpanded = isActiveBaseRoute || (lastLocalStorageValue === null ? expanded : lastLocalStorageValue === "true");
  }
  const group = /* @__PURE__ */ React5.createElement(TacoNavigation.Menu.Group, { ...otherProps, expanded: isExpanded, fixed, onClick, ref });
  if (path) {
    return /* @__PURE__ */ React5.createElement(Authorise, { path }, group);
  }
  return group;
});
var Menu = React5.forwardRef((props, ref) => {
  return /* @__PURE__ */ React5.createElement(TacoNavigation.Menu, { ...props, ref });
});
Menu.Group = MenuGroup;
var Navigation = React5.forwardRef((props, ref) => {
  return /* @__PURE__ */ React5.createElement(TacoNavigation, { ...props, id: "navigation", className: "w-64 z-10 flex-shrink-0 border-r-2 border-grey-light", ref });
});
Navigation.Menu = Menu;
var Content = React5.forwardRef(({ inset = true, ...props }, ref) => /* @__PURE__ */ React5.createElement(ErrorBoundary, null, /* @__PURE__ */ React5.createElement("main", { ...props, className: `bg-white flex-shrink flex-grow overflow-y-auto relative ${inset ? "p-4" : ""}`, ref }, props.children)));
var Layout = (props) => /* @__PURE__ */ React5.createElement("div", { ...props, className: "flex-grow flex overflow-hidden" });
Layout.Navigation = Navigation;
Layout.Content = Content;

// src/components/utils.ts
import { detect, fromNavigator, fromStorage, fromUrl } from "@lingui/detect-locale";

// src/components/constants.ts
var STORAGE_LOCALE_KEY = "locale";
var DEFAULT_FALLBACK_LOCALE = "da-dk";

// src/components/utils.ts
var storeLocale = (locale) => {
  try {
    localStorage.setItem(STORAGE_LOCALE_KEY, locale);
  } catch {
  }
};
var getDetectedLocale = () => {
  const locale = detect(fromUrl("language"), fromStorage(STORAGE_LOCALE_KEY), fromNavigator()) || DEFAULT_FALLBACK_LOCALE;
  return locale;
};
var isDanishLocale = (locale) => locale.startsWith("da");

// src/components/UnexpectedError.tsx
var UnexpectedError = () => {
  const locale = getDetectedLocale();
  const isDanish = isDanishLocale(locale);
  const TITLE = isDanish ? "Der skete en fejl" : "An error occurred";
  const TEXT = isDanish ? "Vi er kede af det - der gik noget galt" : "We're sorry - something went wrong";
  const BUTTON = isDanish ? "Pr\xF8v at genindl\xE6se siden" : "Try reloading the page";
  return /* @__PURE__ */ React6.createElement(Layout, null, /* @__PURE__ */ React6.createElement(Layout.Content, null, /* @__PURE__ */ React6.createElement("div", { className: "p-8 h-full flex flex-col items-center justify-center space-y-4" }, /* @__PURE__ */ React6.createElement(
    "img",
    {
      src: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGIAAABeCAMAAADvwzHqAAABFFBMVEX///8iIiIjIyMkJCQlJSUqKiorKyssLCwuLi4vLy8wMDAyMjI5OTk7Ozs9PT1BQUFCQkJGRkZHR0dMTExOTk5QUFBSUlJTU1NUVFRVVVVWVlZXV1dYWFhZWVldXV1gYGBhYWFiYmJjY2NkZGRmZmZoaGhqampsbGxtbW1ubm5vb29zc3N2dnZ5eXmEhISFhYWGhoaIiIiJiYmLi4uMjIyNjY2YmJiampqbm5ucnJydnZ2enp6goKChoaGnp6esrKyvr6+0tLS4uLi7u7u8vLzExMTMzMzOzs7Pz8/S0tLc3Nze3t7h4eHi4uLl5eXm5ubq6urr6+vt7e3v7+/w8PDx8fHy8vL09PT19fX7+/v+/v7////BjGZ4AAAAAXRSTlMAQObYZgAAAbBJREFUeNrt2dlOwkAUgOEZQRFxAauyWVkFUVGK1YIIKLtoBali5bz/e2i8IDFF4MgMMTj/VdNz8aXL9GJKTC0U8HLLF8oZRFNegWN9tUBCJnDNjBIvcC5AdnkTPkFgid7Bst3GLkf4yULspwxgmJFKWYiVPuM157AQNmCcXRCCEIQgFoIY6PoALFnGMxANN6XuBgAgxjhCd9DPHDoAYowjFPqVAoAY44gk/SoJgBjzuQr+z4L/G8V/XYgPiCAEIYi/QDy3qqWCphVK1dYze8KoZPweOa0Wy7e35aKalj3+TMVgR3TzQUmp9uBbvaoiBfNdJkQ9JmkdGFlHk2L1mYnaTqIJY2omdmozEYNIvA0TasejgxkIMGCKDLH0BLGoxBJ/ws56y2vVQiQYb9wdH1kISNmonV1rJ7rYChYEu+5crjvOhJNS5zwI/jdKvFFYQuL/001+4yuYYXKTfeEp9C+vCClKW3u+SW2MOTe2SJ5MmQzWZDJVghDE/Iiz+N6w9eHRqHPekPorYftahyl7f8yeEnwXKmA6z+GJzQcUcX+IJyigMmU8EQBcgX9LIPuZ+ACynmoZOPGX0QAAAABJRU5ErkJggg==",
      alt: TITLE
    }
  ), /* @__PURE__ */ React6.createElement("h1", null, TITLE), /* @__PURE__ */ React6.createElement("p", null, TEXT), /* @__PURE__ */ React6.createElement(Button, { onClick: () => window.top.location.reload(), appearance: "primary" }, BUTTON))));
};

// src/components/ErrorBoundary.tsx
var ErrorBoundary = class extends React7.Component {
  constructor(props) {
    super(props);
    this.state = { error: void 0 };
  }
  static getDerivedStateFromError(error2) {
    return { error: error2 };
  }
  /**
   * Log error and error info to the Datadog
   * @docs https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
   * @param error {Error}
   * @param errorInfo {ErrorInfo}
   */
  componentDidCatch(error2, errorInfo) {
    const renderingError = new Error(error2.message);
    renderingError.name = `ReactRenderingError`;
    renderingError.stack = errorInfo.componentStack;
    renderingError.cause = error2;
    log_default.error(error2);
  }
  render() {
    const { component: Component = UnexpectedError } = this.props;
    if (this.state.error) {
      return /* @__PURE__ */ React7.createElement(Component, { error: this.state.error });
    }
    return this.props.children;
  }
};

// src/i18n/i18nResource.ts
import { i18n } from "@lingui/core";

// src/tracing/libraries/gtm.ts
var sendVariable = (data = {}) => {
  try {
    if (window.dataLayer) {
      window.dataLayer.push(data);
    } else {
      console.error(
        "Google Tag Manager has not been initialized. You must call `TagManager.initialize('GTM-XXXXX')` in the startup phase of your application before sending any events."
      );
    }
  } catch (error2) {
  }
};
var sendEvent = (event, data = {}) => sendVariable({ event, ...data });

// src/tracing/analytics.ts
var analytics_default = {
  gtm: sendEvent
};

// src/i18n/i18nResource.ts
var FALLBACK_LANGUAGE = "en";
var FALLBACK_LANGUAGE_CODE = "en-GB";
var i18nCache = {};
async function load(languageCode, path = "") {
  const language = languageCode.substring(0, 2);
  const cacheKey = path ? `${path}_${language}` : language;
  if (i18nCache[cacheKey]) {
    return Promise.resolve();
  }
  let messages;
  try {
    if (process.env.NODE_ENV === "production") {
      const response = await import(
        /* webpackIgnore: true */
        `${path}/${language}.mjs`
      );
      messages = response.messages;
    } else {
      const response = await fetch(`${path}/${language}.json`);
      if (response.ok) {
        messages = await response.json();
      } else {
        if (response.status === 404 && languageCode !== FALLBACK_LANGUAGE_CODE) {
          log_default.error(new Error(`Failed to load locale for '${languageCode}', fell back to English instead.`));
          return await load(FALLBACK_LANGUAGE_CODE, path);
        }
        throw new Error(`Failed to fetch ${path}/${language}.json`);
      }
    }
  } catch (error2) {
    if (language !== "en") {
      log_default.error(new Error(`Failed to load locale for '${languageCode}', fell back to English instead.`));
      return load(FALLBACK_LANGUAGE_CODE, path);
    }
    return Promise.reject(`An error occurred fetching translations - ${error2.code}: ${error2.message}`);
  }
  i18n.load(language, messages);
  i18nCache[cacheKey] = messages;
  return Promise.resolve();
}
function init(languageCode) {
  const langCode = languageCode.substring(0, 2);
  const language = langCode || FALLBACK_LANGUAGE;
  i18n.load(language, {});
  i18n.activate(language);
}
var i18nResource_default = {
  i18n,
  init,
  load
};

// src/components/Host/useHostContext.tsx
import React9 from "react";

// src/components/Host/HostContext.tsx
import React8 from "react";
var HostContext = React8.createContext(null);

// src/components/Host/useHostContext.tsx
function useHostContext() {
  const context = React9.useContext(HostContext);
  if (!context) {
    throw new Error("useHostContext must be used within a HostContext.Provider");
  }
  return context;
}

// src/hooks/useCurrentLocale.ts
var useCurrentLocale = () => {
  const { identity } = useHostContext();
  return identity.locale;
};

// src/components/Application/ApplicationProvider.tsx
import React10 from "react";
import { Spinner } from "@economic/taco";
var ApplicationProvider = ({ children, value }) => {
  return /* @__PURE__ */ React10.createElement(ErrorBoundary, null, /* @__PURE__ */ React10.createElement(React10.Suspense, { fallback: /* @__PURE__ */ React10.createElement(Spinner, { className: "w-full p-8" }) }, /* @__PURE__ */ React10.createElement(ApplicationContext.Provider, { value }, children)));
};

// src/components/Application/Application.tsx
var Application = ({ basename, children, configuration, origin, version }) => {
  const [context, setContext] = React11.useState();
  const locale = useCurrentLocale();
  const init3 = async () => {
    const localesPath = `${origin ?? ""}/locales`;
    try {
      const [response] = await Promise.all([configuration(), i18nResource_default.load(locale.languageCode, localesPath)]);
      setContext({ basename, configuration: response, version });
    } catch (e) {
      log_default.error(e);
    }
  };
  React11.useEffect(() => {
    init3();
    if (!window.top.mfe_applicationFetchers) {
      window.top.mfe_applicationFetchers = [];
    }
    window.top.mfe_applicationFetchers.push(init3);
  }, [locale]);
  if (!context) {
    return /* @__PURE__ */ React11.createElement(Spinner2, { className: "w-full p-8" });
  }
  return /* @__PURE__ */ React11.createElement(ErrorBoundary, null, /* @__PURE__ */ React11.createElement(React11.Suspense, { fallback: /* @__PURE__ */ React11.createElement(Spinner2, { className: "w-full p-8" }) }, /* @__PURE__ */ React11.createElement(ApplicationProvider, { value: context }, children)));
};

// src/components/Host/Host.tsx
import React16 from "react";
import { I18nProvider } from "@lingui/react";
import { Spinner as Spinner3 } from "@economic/taco";

// src/datadog.ts
import { datadogRum as datadogRum2 } from "@datadog/browser-rum";
var cleanDatadogEvent = (event) => {
  switch (event.type) {
    case "view":
      event.view.url = event.view.url.replace(/email=[^&]*/, "email=REDACTED");
      break;
    case "resource":
      if (event.resource) {
        event.resource.url = event.resource.url.replace(/email=[^&]*/, "email=REDACTED").replace(/emailAddress=[^&]*/, "emailAddress=REDACTED").replace(/key=[^&]*/, "key=REDACTED").replace(/gsessionid=[^&]*/, "gsessionid=REDACTED").replace(/X-Amz-Signature=[^&]*/, "X-Amz-Signature=REDACTED");
      }
      break;
    default:
      break;
  }
};
var sampleRateEnvMap = {
  production: 50,
  "staging-brb": 100,
  "staging-qaplatform1": 100
};
var datadogInit = (applicationId, clientToken, environment, version) => {
  datadogRum2.init({
    applicationId,
    clientToken,
    site: "datadoghq.eu",
    service: "e-conomic",
    env: environment,
    version,
    sessionSampleRate: sampleRateEnvMap[environment] ?? 0,
    /*
        This is done to optimize the cost, by distributing to match our sessions commitment in our plan:
        2m sessions/month without replay and 3m sessions/month with replay.
        This means we aim to sample 2/3 of the sessions with replays.
    */
    sessionReplaySampleRate: 67,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: "mask",
    allowedTracingUrls: ["https://e-conomic.com", /https:\/\/.*\.e-conomic\.(com|ws)/],
    trackSessionAcrossSubdomains: true,
    useSecureSessionCookie: true,
    silentMultipleInit: true,
    enableExperimentalFeatures: ["feature_flags"],
    beforeSend: (event) => {
      if (!(environment.startsWith("staging-") || environment === "production")) {
        return false;
      }
      cleanDatadogEvent(event);
      return true;
    }
  });
};
var setDatadogUser = (identity) => {
  datadogRum2.setUser({
    id: `${identity.agreement.number}_${identity.user.id}`,
    name: identity.user.id
  });
};
var addDatadogFeatureFlagEvaluation = (featureName, value) => {
  const normalized = featureName.replace(/[.:+\-=><!(){}[\]^"“”~*?\\]|&&|\|\|/g, "_");
  datadogRum2.addFeatureFlagEvaluation(normalized, value);
};

// src/components/Host/HostProvider.tsx
import React14 from "react";

// src/components/Host/useFirestore.ts
import React12 from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/performance";
import "firebase/compat/auth";
import "firebase/compat/firestore";
var initEmulator = (config) => {
  const { authHost: emulatorAuthHost, host: emulatorHost } = config;
  firebase.firestore().settings({ experimentalForceLongPolling: true });
  if (emulatorAuthHost) {
    const authHostParts = emulatorAuthHost.split(":");
    if (authHostParts.length !== 2) {
      throw new Error("Firebase auth emulator host - bad format");
    }
    firebase.auth().useEmulator(`http://${authHostParts[0]}:${authHostParts[1]}/`);
  }
  if (emulatorHost) {
    const hostParts = emulatorHost.split(":");
    if (hostParts.length !== 2) {
      throw new Error("Firestore emulator host - bad format");
    }
    firebase.firestore().useEmulator(hostParts[0], parseInt(hostParts[1]));
  }
};
var initWithToken = async (token, identity) => {
  await firebase.auth().signInWithCustomToken(token);
  if (!firebase.apps.length) {
    throw new Error(`Couldn't authenticate firestore`);
  }
  const db = firebase.firestore();
  const agreement = db.collection("agreements").doc(String(identity.agreement.number));
  const agreementDocument = await agreement.get();
  if (!agreementDocument.exists) {
    await agreement.set({});
    await agreement.collection("users").doc(identity.user.guid).set({});
  }
};
var useFirestore = (configuration, identity, environmentType, emulator) => {
  React12.useEffect(() => {
    if (!firebase.apps.length) {
      firebase.initializeApp({
        projectId: configuration.projectId,
        authDomain: configuration.authDomain,
        apiKey: configuration.apiKey,
        appId: configuration.appId
      });
      if (environmentType === "staging" || environmentType === "production") {
        firebase.performance();
      }
      if (emulator) {
        initEmulator(emulator);
      }
      initWithToken(configuration.initialToken, identity);
    }
  }, []);
};

// src/components/Host/useGoogleTagManager.ts
import React13 from "react";
var init2 = ({ containerId, auth, previewEnvironment }) => {
  const code = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'//www.googletagmanager.com/gtm.js?id='+i+dl+'&gtm_auth=${auth}&gtm_preview=${previewEnvironment}&gtm_cookies_win=x';
f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
  const script = document.createElement("script");
  script.innerHTML = code;
  document.head.appendChild(script);
};
var useGoogleTagManager = (configuration) => {
  React13.useEffect(() => {
    init2(configuration);
    sendVariable(configuration.data);
  }, []);
};

// src/components/Host/HostProvider.tsx
var HostProvider = ({ children, value, firebaseEmulator }) => {
  useFirestore(value.configuration.integrations.firestore, value.identity, value.environmentType, firebaseEmulator);
  useGoogleTagManager(value.configuration.integrations.googleTagManager);
  return /* @__PURE__ */ React14.createElement(HostContext.Provider, { value }, children);
};

// src/components/CriticalError.tsx
import React15 from "react";
import { Button as Button2 } from "@economic/taco";
var CriticalError = () => {
  const locale = getDetectedLocale();
  const isDanish = isDanishLocale(locale);
  const TITLE = isDanish ? "Der skete en fejl" : "An error occurred";
  const TEXT = isDanish ? "Vi er kede af det - der gik noget galt" : "We're sorry - something went wrong";
  const BUTTON = isDanish ? "Pr\xF8v at genindl\xE6se siden" : "Try reloading the page";
  const STATUS_PAGE_TEXT = isDanish ? "Du kan finde mere information p\xE5 vores statusside:" : "For more information, please visit our status page:";
  return /* @__PURE__ */ React15.createElement(Layout, null, /* @__PURE__ */ React15.createElement(Layout.Content, null, /* @__PURE__ */ React15.createElement("div", { className: "p-8 h-full flex flex-col items-center justify-center space-y-4" }, /* @__PURE__ */ React15.createElement(
    "img",
    {
      src: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGIAAABeCAMAAADvwzHqAAABFFBMVEX///8iIiIjIyMkJCQlJSUqKiorKyssLCwuLi4vLy8wMDAyMjI5OTk7Ozs9PT1BQUFCQkJGRkZHR0dMTExOTk5QUFBSUlJTU1NUVFRVVVVWVlZXV1dYWFhZWVldXV1gYGBhYWFiYmJjY2NkZGRmZmZoaGhqampsbGxtbW1ubm5vb29zc3N2dnZ5eXmEhISFhYWGhoaIiIiJiYmLi4uMjIyNjY2YmJiampqbm5ucnJydnZ2enp6goKChoaGnp6esrKyvr6+0tLS4uLi7u7u8vLzExMTMzMzOzs7Pz8/S0tLc3Nze3t7h4eHi4uLl5eXm5ubq6urr6+vt7e3v7+/w8PDx8fHy8vL09PT19fX7+/v+/v7////BjGZ4AAAAAXRSTlMAQObYZgAAAbBJREFUeNrt2dlOwkAUgOEZQRFxAauyWVkFUVGK1YIIKLtoBali5bz/e2i8IDFF4MgMMTj/VdNz8aXL9GJKTC0U8HLLF8oZRFNegWN9tUBCJnDNjBIvcC5AdnkTPkFgid7Bst3GLkf4yULspwxgmJFKWYiVPuM157AQNmCcXRCCEIQgFoIY6PoALFnGMxANN6XuBgAgxjhCd9DPHDoAYowjFPqVAoAY44gk/SoJgBjzuQr+z4L/G8V/XYgPiCAEIYi/QDy3qqWCphVK1dYze8KoZPweOa0Wy7e35aKalj3+TMVgR3TzQUmp9uBbvaoiBfNdJkQ9JmkdGFlHk2L1mYnaTqIJY2omdmozEYNIvA0TasejgxkIMGCKDLH0BLGoxBJ/ws56y2vVQiQYb9wdH1kISNmonV1rJ7rYChYEu+5crjvOhJNS5zwI/jdKvFFYQuL/001+4yuYYXKTfeEp9C+vCClKW3u+SW2MOTe2SJ5MmQzWZDJVghDE/Iiz+N6w9eHRqHPekPorYftahyl7f8yeEnwXKmA6z+GJzQcUcX+IJyigMmU8EQBcgX9LIPuZ+ACynmoZOPGX0QAAAABJRU5ErkJggg==",
      alt: "An error occurred"
    }
  ), /* @__PURE__ */ React15.createElement("h1", null, TITLE), /* @__PURE__ */ React15.createElement("p", null, TEXT), /* @__PURE__ */ React15.createElement(Button2, { onClick: () => window.top.location.reload(), appearance: "primary" }, BUTTON), /* @__PURE__ */ React15.createElement("p", { className: "text-small" }, STATUS_PAGE_TEXT, " ", /* @__PURE__ */ React15.createElement("a", { href: "https://status.e-conomic.com/" }, "https://status.e-conomic.com/"), "."))));
};

// src/components/Host/Host.tsx
var Host = ({ children, configuration, environmentType, environment, origin, version, onFlagExposed }) => {
  const [context, setContext] = React16.useState();
  const [error2, setError] = React16.useState(null);
  const init3 = async () => {
    datadogInit(configuration.datadog.applicationId, configuration.datadog.clientToken, environment, version);
    try {
      const response = await configuration.fetcher();
      const { identity } = response;
      await loadI18n(identity.locale.languageCode, origin);
      setDatadogUser(response.identity);
      const refetch = async () => {
        return Promise.all([
          window.top.mfe_hostFetcher(),
          ...window.top.mfe_applicationFetchers.map((fetcher) => fetcher())
        ]);
      };
      setContext({ ...response, environmentType, refetch, version, onFlagExposed });
    } catch (error3) {
      if (error3?.response?.status === 401) {
        window.location.replace(`${error3.response.data}?returnUrl=${encodeURIComponent(window.location.href)}`);
      } else {
        setError(error3);
        log_default.error(error3);
      }
    }
  };
  React16.useEffect(() => {
    init3();
    if (!window.top.mfe_hostFetcher) {
      window.top.mfe_hostFetcher = init3;
    }
  }, []);
  if (error2) {
    return /* @__PURE__ */ React16.createElement(CriticalError, null);
  }
  if (!context) {
    return /* @__PURE__ */ React16.createElement("div", { className: "flex justify-center loader w-full top-0 absolute left-0 h-full bottom-0 bg-white" }, /* @__PURE__ */ React16.createElement(Spinner3, null));
  }
  return /* @__PURE__ */ React16.createElement(I18nProvider, { i18n: i18nResource_default.i18n }, /* @__PURE__ */ React16.createElement(HostProvider, { value: context, firebaseEmulator: configuration.firebaseEmulator }, children));
};
var loadI18n = async (languageCode, origin = "") => {
  const localesPath = `${origin ?? ""}/locales`;
  storeLocale(languageCode);
  i18nResource_default.init(languageCode);
  await i18nResource_default.load(languageCode, localesPath);
};

// src/components/LazyRemote.tsx
import React17 from "react";
function loadComponent(scope, module) {
  return async () => {
    await __webpack_init_sharing__("default");
    const container = window[scope];
    await container.init(__webpack_share_scopes__.default);
    const factory = await window[scope].get(module);
    const Module = factory();
    return Module;
  };
}
var useDynamicScript = (url) => {
  const [ready, setReady] = React17.useState(!!remotes[url] || false);
  const [failed, setFailed] = React17.useState(false);
  React17.useEffect(() => {
    if (!url || ready) {
      return;
    }
    if (document.head.querySelector(`script[src='${url}]`)) {
      setReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = url;
    script.type = "text/javascript";
    script.async = true;
    setReady(false);
    setFailed(false);
    script.onload = () => {
      setReady(true);
    };
    script.onerror = () => {
      setReady(false);
      setFailed(true);
    };
    document.head.appendChild(script);
  }, []);
  return {
    ready,
    failed
  };
};
var remotes = {};
var LazyRemote = ({ name, origin, ...props }) => {
  const url = `${origin}/remoteEntry.js`;
  const { ready, failed } = useDynamicScript(url);
  if (!ready) {
    return null;
  }
  if (failed) {
    throw new Error(`Failed to load dynamic script: {url}`);
  }
  if (!remotes[url]) {
    remotes[url] = React17.lazy(loadComponent(name.replace(/[^a-zA-Z ]/g, ""), "./Application"));
  }
  const Component = remotes[url];
  return /* @__PURE__ */ React17.createElement(Component, { ...props, origin });
};

// src/hooks/useApplication.ts
var useApplication = () => {
  const { configuration } = useApplicationContext();
  return configuration;
};

// src/hooks/useCurrentAgreement.ts
var useCurrentAgreement = () => {
  const { identity } = useHostContext();
  return identity?.agreement;
};

// src/hooks/useCurrentUser.ts
var useCurrentUser = () => {
  const { identity } = useHostContext();
  return identity?.user;
};

// src/hooks/useEnvironment.ts
var useEnvironment = () => {
  const { environmentType } = useHostContext();
  return environmentType;
};

// src/hooks/useHost.ts
var useHost = () => {
  const { configuration } = useHostContext();
  return configuration;
};

// src/hooks/useHostRefetch.ts
var useHostRefetch = () => {
  const { refetch } = useHostContext();
  return refetch;
};

// src/legacy/LegacyFrame.tsx
import React18 from "react";
var firebirdReadyResolver;
window.firebirdTask = new Promise((resolve, _) => {
  firebirdReadyResolver = resolve;
});
var LegacyFrame = ({ basePath }) => {
  React18.useEffect(() => {
    window.top.CLIENT_INTEGRATED = true;
    const FIREBIRD_VITAL_NAME = "firebird_ready";
    log_default.customVital.startDurationVital(FIREBIRD_VITAL_NAME);
    const timeoutId = setTimeout(() => {
      log_default.customVital.stopDurationVital(FIREBIRD_VITAL_NAME);
      log_default.timing("firebird_timeout");
    }, 60 * 1e3);
    window.top.addEventListener("firebird:ready", () => {
      clearTimeout(timeoutId);
      log_default.customVital.stopDurationVital(FIREBIRD_VITAL_NAME);
      log_default.timing("firebird_ready");
      window.top.Econ = window.firebird.Econ;
      window.top.PopUp2 = window.firebird.PopUp2;
      window.top.Econ.on("entrycounter.updatesingledaybook", () => window.top.refetch("/journals/totals/draft"));
      window.top.Econ.on("errorstatus.updatesent", () => window.top.refetch("/sales/invoicing/sent/errors"));
      if (firebirdReadyResolver) {
        firebirdReadyResolver();
      }
    });
  }, []);
  let inAppInstrumentation = "";
  if (window.location.search && window.location.search.toLowerCase().includes("inappinstrumentation")) {
    inAppInstrumentation = "?InAppInstrumentation=true";
  }
  return /* @__PURE__ */ React18.createElement(
    "iframe",
    {
      name: "firebird",
      className: "border-none hidden h-full w-full absolute top-0 left-0 z-0",
      src: `${basePath}/default.aspx${inAppInstrumentation}`,
      title: "legacy"
    }
  );
};

// src/legacy/LegacyFrameNavigator.tsx
import React20 from "react";
import debounce from "lodash.debounce";
import { useLocation as useLocation2, useParams } from "react-router-dom";
import { Spinner as Spinner4 } from "@economic/taco";

// src/routing/NotFound.tsx
import React19 from "react";
import { useNavigate } from "react-router-dom";
import { Button as Button3, Group } from "@economic/taco";
import { Trans, useLingui } from "@lingui/react";
var NotFound = () => {
  const navigate = useNavigate();
  const { i18n: i18n2 } = useLingui();
  return /* @__PURE__ */ React19.createElement(Layout, null, /* @__PURE__ */ React19.createElement(Layout.Content, null, /* @__PURE__ */ React19.createElement("div", { className: "p-8 h-full flex flex-col items-center justify-center space-y-4" }, /* @__PURE__ */ React19.createElement(
    "img",
    {
      src: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGIAAABeCAMAAADvwzHqAAABFFBMVEX///8iIiIjIyMkJCQlJSUqKiorKyssLCwuLi4vLy8wMDAyMjI5OTk7Ozs9PT1BQUFCQkJGRkZHR0dMTExOTk5QUFBSUlJTU1NUVFRVVVVWVlZXV1dYWFhZWVldXV1gYGBhYWFiYmJjY2NkZGRmZmZoaGhqampsbGxtbW1ubm5vb29zc3N2dnZ5eXmEhISFhYWGhoaIiIiJiYmLi4uMjIyNjY2YmJiampqbm5ucnJydnZ2enp6goKChoaGnp6esrKyvr6+0tLS4uLi7u7u8vLzExMTMzMzOzs7Pz8/S0tLc3Nze3t7h4eHi4uLl5eXm5ubq6urr6+vt7e3v7+/w8PDx8fHy8vL09PT19fX7+/v+/v7////BjGZ4AAAAAXRSTlMAQObYZgAAAbBJREFUeNrt2dlOwkAUgOEZQRFxAauyWVkFUVGK1YIIKLtoBali5bz/e2i8IDFF4MgMMTj/VdNz8aXL9GJKTC0U8HLLF8oZRFNegWN9tUBCJnDNjBIvcC5AdnkTPkFgid7Bst3GLkf4yULspwxgmJFKWYiVPuM157AQNmCcXRCCEIQgFoIY6PoALFnGMxANN6XuBgAgxjhCd9DPHDoAYowjFPqVAoAY44gk/SoJgBjzuQr+z4L/G8V/XYgPiCAEIYi/QDy3qqWCphVK1dYze8KoZPweOa0Wy7e35aKalj3+TMVgR3TzQUmp9uBbvaoiBfNdJkQ9JmkdGFlHk2L1mYnaTqIJY2omdmozEYNIvA0TasejgxkIMGCKDLH0BLGoxBJ/ws56y2vVQiQYb9wdH1kISNmonV1rJ7rYChYEu+5crjvOhJNS5zwI/jdKvFFYQuL/001+4yuYYXKTfeEp9C+vCClKW3u+SW2MOTe2SJ5MmQzWZDJVghDE/Iiz+N6w9eHRqHPekPorYftahyl7f8yeEnwXKmA6z+GJzQcUcX+IJyigMmU8EQBcgX9LIPuZ+ACynmoZOPGX0QAAAABJRU5ErkJggg==",
      alt: i18n2._(`Page not found`)
    }
  ), /* @__PURE__ */ React19.createElement("h1", null, /* @__PURE__ */ React19.createElement(Trans, { id: "Page not found" })), /* @__PURE__ */ React19.createElement("p", null, /* @__PURE__ */ React19.createElement(Trans, { id: "Unfortunately we couldn't find the page you're looking for" })), /* @__PURE__ */ React19.createElement(Group, null, /* @__PURE__ */ React19.createElement(Button3, { appearance: "primary", onClick: () => navigate(-1) }, /* @__PURE__ */ React19.createElement(Trans, { id: "Go back" })), /* @__PURE__ */ React19.createElement(Button3, { appearance: "primary", onClick: () => navigate("/") }, /* @__PURE__ */ React19.createElement(Trans, { id: "Go to home" }))))));
};

// src/legacy/LegacyFrameNavigator.tsx
var setFirebirdWidth = debounce(() => {
  const frame = window.firebird.document.querySelector("#midnav-frame");
  const navigation = window.top.document.querySelector("#navigation");
  let width = "100%";
  if (navigation) {
    const isResponsive = navigation.getAttribute("data-responsive") === "true";
    const isHidden = navigation.getAttribute("aria-hidden") === "true";
    if (!isResponsive && !isHidden) {
      width = `calc(100% - ${navigation.offsetWidth}px - 2px)`;
    }
  }
  if (frame) {
    frame.style.width = width;
  }
}, 100);
var replaceParameters = (url, params) => Object.keys(params).reduce((u, k) => u.replace(`:${k}`, params[k]), url);
var midnavWhitelist = ["/home", "/inbox", "/market", "/journal-pro", "/webshop", "/webshop-plus", "/website"];
var refreshCurrentLegacyFrame = (frame = "midnav") => {
  window.top.firebird.Econ.frames[frame].location.reload();
};
var refreshCurrentLegacyPage = (frame = "midnav") => {
  window.top.firebird.Econ.frames[frame].location.reload();
};
var redirectToLegacyPage = (legacyUrl, frame = "content") => {
  window.top.firebird.Econ.frames[frame].location.href = legacyUrl;
};
var getLegacyUrl = (legacyUrl, params, location) => {
  const url = replaceParameters(legacyUrl, params);
  const queryString = location.search ? url.includes("?") ? location.search.replace("?", "&") : location.search : void 0;
  return url + (queryString || "");
};
var previousPath = "";
var FrameNavigator = class extends React20.Component {
  state = { hasNavigated: false, isLoading: true, notFound: false };
  componentDidMount() {
    if (!window.top.Econ) {
      window.top.addEventListener("firebird:ready", this.handleFirebirdReady);
    } else {
      this.bindFrameListeners("on");
      if (midnavWhitelist.filter((x) => previousPath.startsWith(x)).length > 0) {
        window.firebird.Econ.require("frames").navigate("midnav", "midnav.aspx").then(() => {
          this.navigate();
        });
      } else {
        this.navigate();
      }
    }
    window.top.addEventListener("resize", setFirebirdWidth);
  }
  shouldComponentUpdate(nextProps, nextState) {
    const changedUrl = this.props.location.pathname !== nextProps.location.pathname;
    const reloadedCurrentUrl = !changedUrl && this.props.location.key !== nextProps.location.key;
    const changedHash = this.props.location.hash !== nextProps.location.hash;
    if (changedUrl || reloadedCurrentUrl && !changedHash || this.state.isLoading !== nextState.isLoading || this.state.notFound !== nextState.notFound) {
      document.querySelector("iframe[name=firebird]").style.display = "none";
      return true;
    }
    return false;
  }
  componentDidUpdate(prevProps, prevState) {
    if (window.top.Econ) {
      if (this.props.location.key !== prevProps.location.key && (this.props.legacyUrl !== prevProps.legacyUrl || this.props.location.hash === "")) {
        this.setState({ hasNavigated: false, isLoading: true }, this.navigate);
      }
    }
    if (this.state.notFound) {
      document.querySelector("iframe[name=firebird]").style.display = "none";
    } else if (prevState.isLoading && !this.state.isLoading) {
      document.querySelector("iframe[name=firebird]").style.display = "block";
    }
  }
  componentWillUnmount() {
    document.querySelector("iframe[name=firebird]").style.display = "none";
    window.top.removeEventListener("resize", setFirebirdWidth);
    window.top.removeEventListener("firebird:ready", this.handleFirebirdReady);
    if (window.top.Econ) {
      this.bindFrameListeners("off");
    }
  }
  render() {
    if (this.state.isLoading) {
      return /* @__PURE__ */ React20.createElement(Spinner4, { className: "w-full p-8" });
    }
    if (this.state.notFound) {
      return /* @__PURE__ */ React20.createElement(NotFound, null);
    }
    return null;
  }
  handleFirebirdReady = (event) => {
    if (event.detail.isGettingStartedSplashScreenAvailable) {
      this.displayFrameForGettingStartedGuide();
    } else {
      this.bindFrameListeners("on");
      window.firebird.Econ.require("frames").navigate("midnav", "midnav.aspx").then(() => {
        this.navigate();
      });
    }
  };
  displayFrameForGettingStartedGuide = () => {
    this.setState({ isLoading: false });
  };
  notFound = () => {
    document.querySelector("iframe[name=firebird]").style.display = "none";
    this.setState({ isLoading: false, notFound: true });
  };
  navigate = () => {
    const { params, legacyUrl, location } = this.props;
    const frames = window.firebird.Econ.require("frames");
    const frame = midnavWhitelist.filter((x) => location.pathname.startsWith(x)).length > 0 ? "midnav" : "content";
    if (frames.inMidnav("content")) {
      frames.setRightFrames(true);
    }
    frames.navigate(frame, getLegacyUrl(legacyUrl, params, location));
    previousPath = location.pathname;
    this.setState({ hasNavigated: true });
    setFirebirdWidth();
  };
  bindFrameListeners = (action) => {
    window.top.Econ[action]("frame.load.midnav", this.handleMidnavReady);
    window.top.Econ[action]("frame.load.content", this.handleContentReady);
    window.top.Econ[action]("frame.load.applet", this.handleAppletReady);
  };
  handleMidnavReady = () => {
    const applet = window.firebird.midnav.document.querySelector("iframe[name=applet]");
    const content = window.firebird.midnav.document.querySelector("iframe[name=content]");
    if (!applet && !content) {
      this.setRouteAsReady();
      if (this.props.onNavigated) {
        this.props.onNavigated("midnav");
      }
    }
  };
  handleContentReady = () => {
    const { params, legacyUrl, location } = this.props;
    const url = getLegacyUrl(legacyUrl, params, location);
    if (this.state.hasNavigated && !url.includes("tomform.asp")) {
      this.setRouteAsReady();
      if (this.props.onNavigated) {
        this.props.onNavigated("content");
      }
    }
  };
  handleAppletReady = () => {
    if (this.state.hasNavigated) {
      this.setRouteAsReady();
    }
  };
  // this occurs when midnav and/or applet frames are 'ready'
  setRouteAsReady = () => {
    this.setState({ isLoading: false });
  };
};
var LegacyFrameNavigator = (props) => {
  const location = useLocation2();
  const params = useParams();
  return /* @__PURE__ */ React20.createElement(FrameNavigator, { ...props, location, params });
};

// src/legacy/useLegacyFrame.tsx
import React21 from "react";
function useLegacyFrame() {
  const [show, setShow] = React21.useState(false);
  React21.useEffect(() => {
    let mounted = true;
    window.top.firebirdTask.then(() => {
      if (mounted) {
        setShow(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);
  return {
    show
  };
}

// src/routing/AuthDialogRoute.tsx
import React23 from "react";
import { useNavigate as useNavigate2 } from "react-router-dom";
import { Dialog, Spinner as Spinner5 } from "@economic/taco";

// src/routing/usePathAuthorisation.ts
import React22 from "react";
import { useLocation as useLocation3 } from "react-router-dom";
var __internalState = {
  routeParts: [],
  pathname: ""
};
var createRoutePath = (basename) => {
  return `/${(basename ? __internalState.routeParts.filter((part) => part !== basename) : __internalState.routeParts).join("/")}`;
};
var useAbsolutePath = (segment) => {
  const { pathname } = useLocation3();
  const { basename } = useApplicationContext();
  if (__internalState.pathname !== pathname) {
    __internalState.pathname = pathname;
    __internalState.routeParts = [];
  }
  const sanitizedPath = segment.replace("/*", "");
  const occurencesInPathname = pathname.split("/").filter((segment2) => segment2 === sanitizedPath).length;
  const occurencesInParts = __internalState.routeParts.filter((x) => x === sanitizedPath).length;
  const isPartsSynced = !(occurencesInPathname > 0 && occurencesInParts < occurencesInPathname);
  if (sanitizedPath !== "/" && (!isPartsSynced || !__internalState.routeParts.includes(sanitizedPath))) {
    __internalState.routeParts.push(sanitizedPath);
  }
  const routePath = createRoutePath(basename);
  return routePath.length > 1 && routePath.endsWith("/") ? routePath.replace(/\/+$/, "") : routePath;
};
var usePathAuthorisation = (segment) => {
  const { configuration } = useApplicationContext();
  const currentRoute = useAbsolutePath(segment);
  const authorisedRoute = React22.useMemo(
    () => matchRoute(Object.keys(configuration.routes), currentRoute),
    [configuration.routes, currentRoute]
  );
  const legacyUrl = authorisedRoute ? configuration.routes[authorisedRoute] : null;
  return [!!authorisedRoute, currentRoute, legacyUrl];
};

// src/routing/AuthDialogRoute.tsx
var AuthDialogRoute = (props) => {
  const { "aria-label": ariaLabel, caseSensitive, element, path, ...dialogProps } = props;
  const [authorised] = usePathAuthorisation(path);
  const navigate = useNavigate2();
  let output;
  if (authorised) {
    output = element;
  } else {
    output = /* @__PURE__ */ React23.createElement(NotFound, null);
  }
  return /* @__PURE__ */ React23.createElement(Dialog, { ...dialogProps, onClose: () => navigate(-1), defaultOpen: true }, /* @__PURE__ */ React23.createElement(Dialog.Content, { "aria-label": ariaLabel }, /* @__PURE__ */ React23.createElement(React23.Suspense, { fallback: /* @__PURE__ */ React23.createElement(Spinner5, { className: "w-full p-4" }) }, /* @__PURE__ */ React23.createElement(ErrorBoundary, null, output))));
};

// src/routing/AuthorisedLink.tsx
import React24 from "react";
import { matchPath, useHref as useHref2, useLocation as useLocation4, useNavigate as useNavigate3 } from "react-router-dom";
function isModifiedEvent(event) {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}
var AuthorisedLinkByPath = React24.forwardRef((props, ref) => {
  const { to, ...otherProps } = props;
  const href = useHref2(to);
  const route = useHref2((otherProps.path ?? to).split("?")[0]);
  const [authorised] = useRouteAuthorisation(route);
  if (!authorised) {
    return null;
  }
  return /* @__PURE__ */ React24.createElement(AuthorisedLinkByRoute, { ...otherProps, to: href, ref });
});
var AuthorisedLinkByRoute = React24.forwardRef((props, ref) => {
  const { active, as: Component = "a", onClick, path, state, target, to, ...otherProps } = props;
  const href = useHref2(to);
  const navigate = useNavigate3();
  const route = useHref2((path ?? to).split("?")[0]);
  const [authorised] = useRouteAuthorisation(route);
  const location = useLocation4();
  const { configuration } = useApplicationContext();
  if (!authorised) {
    return null;
  }
  if (to.toString().startsWith("http")) {
    return /* @__PURE__ */ React24.createElement(
      Component,
      {
        ...otherProps,
        href: to.toString(),
        onClick,
        ref,
        rel: "noopener noreferrer",
        target
      }
    );
  }
  const isExactRoute = !!matchPath({ path: href }, location.pathname + location.search);
  const isOneRoute = Object.keys(configuration.routes).filter((route2) => route2.startsWith(href)).length === 1;
  const isActive = active ?? (isExactRoute || location.pathname.startsWith(href) && isOneRoute);
  const handleClick = (event) => {
    if (onClick) {
      event.persist();
      onClick(event);
    }
    if (!event.defaultPrevented && // onClick prevented default
    event.button === 0 && // Ignore everything but left clicks
    (!target || target === "_self") && // Let browser handle "target=_blank" etc.
    !isModifiedEvent(event)) {
      event.preventDefault();
      navigate(to, { state });
    }
  };
  return /* @__PURE__ */ React24.createElement(
    Component,
    {
      ...otherProps,
      active: Component !== "a" ? !!isActive : void 0,
      href,
      onClick: handleClick,
      ref,
      target
    }
  );
});
var AuthorisedLink = React24.forwardRef((props, ref) => {
  if (props.to.startsWith("/") || props.to.startsWith("http")) {
    return /* @__PURE__ */ React24.createElement(AuthorisedLinkByRoute, { ...props, ref });
  }
  return /* @__PURE__ */ React24.createElement(AuthorisedLinkByPath, { ...props, ref });
});

// src/routing/AuthorisedNavigate.tsx
import React25 from "react";
import { Navigate, useLocation as useLocation5 } from "react-router-dom";
var AuthorisedNavigate = (props) => {
  const { state, to, toFirstAuthorisedSubRoute } = props;
  const { configuration } = useApplicationContext();
  const location = useLocation5();
  const route = configuration.routes[location.pathname];
  if (route?.startsWith("/")) {
    return /* @__PURE__ */ React25.createElement(Navigate, { to: route, state });
  }
  if (toFirstAuthorisedSubRoute) {
    const firstAuthorisedSubRoute = Object.keys(configuration.routes).find(
      (route2) => route2.startsWith(location.pathname) && route2 !== location.pathname
    );
    if (firstAuthorisedSubRoute) {
      return /* @__PURE__ */ React25.createElement(Navigate, { replace: true, to: firstAuthorisedSubRoute, state });
    }
    return /* @__PURE__ */ React25.createElement(NotFound, null);
  }
  if (to) {
    return /* @__PURE__ */ React25.createElement(Navigate, { to, state });
  }
  return null;
};

// src/routing/AuthorisedRoute.tsx
import React26 from "react";
import { Route as BaseRoute } from "react-router-dom";
import { Spinner as Spinner6 } from "@economic/taco";
var AuthorisedRoute = (props) => {
  const { onNavigated, ...otherProps } = props;
  const [authorised, , legacyUrl] = usePathAuthorisation(props.path);
  let output = null;
  if (authorised) {
    if (otherProps.element || otherProps.children) {
      output = /* @__PURE__ */ React26.createElement(BaseRoute, { ...otherProps });
    } else if (legacyUrl) {
      output = /* @__PURE__ */ React26.createElement(
        AuthorisedRoute,
        {
          ...otherProps,
          element: /* @__PURE__ */ React26.createElement(LegacyFrameNavigator, { onNavigated, legacyUrl })
        }
      );
    } else {
      console.error(
        `${otherProps.path} does not have an accompanying legacy url defined, nor does it have a route element.`
      );
    }
  } else {
    output = /* @__PURE__ */ React26.createElement(NotFound, null);
  }
  return /* @__PURE__ */ React26.createElement(React26.Suspense, { fallback: /* @__PURE__ */ React26.createElement(Spinner6, { className: "w-full p-4" }) }, /* @__PURE__ */ React26.createElement(ErrorBoundary, null, output));
};

// src/routing/AuthRoute.tsx
import { Spinner as Spinner7 } from "@economic/taco";
import React27 from "react";
var AuthRoute = (props) => {
  const { onNavigated, ...otherProps } = props;
  const [authorised, , legacyUrl] = usePathAuthorisation(props.path);
  if (!authorised) return /* @__PURE__ */ React27.createElement(NotFound, null);
  if (otherProps.element || otherProps.children) {
    return /* @__PURE__ */ React27.createElement(React27.Suspense, { fallback: /* @__PURE__ */ React27.createElement(Spinner7, { className: "w-full p-4" }) }, /* @__PURE__ */ React27.createElement(ErrorBoundary, null, otherProps.element || otherProps.children));
  }
  if (!legacyUrl) {
    console.error(`${otherProps.path} does not have an accompanying legacy url defined, nor does it have a route element.`);
    return null;
  }
  return /* @__PURE__ */ React27.createElement(AuthRoute, { ...otherProps, element: /* @__PURE__ */ React27.createElement(LegacyFrameNavigator, { onNavigated, legacyUrl }) });
};

// src/routing/AuthRoutes.tsx
import React29 from "react";
import { Route, Routes } from "react-router-dom";

// src/routing/AuthorisedDialogRoute.tsx
import React28 from "react";
import { Route as BaseRoute2, useNavigate as useNavigate4 } from "react-router-dom";
import { Dialog as Dialog2, Spinner as Spinner8 } from "@economic/taco";
var AuthorisedDialogRoute = (props) => {
  const { "aria-label": ariaLabel, caseSensitive, element, path, ...dialogProps } = props;
  const [authorised] = usePathAuthorisation(path);
  const navigate = useNavigate4();
  let output;
  if (authorised) {
    output = /* @__PURE__ */ React28.createElement(BaseRoute2, { caseSensitive, element, path });
  } else {
    output = /* @__PURE__ */ React28.createElement(NotFound, null);
  }
  return /* @__PURE__ */ React28.createElement(Dialog2, { ...dialogProps, onClose: () => navigate(-1), defaultOpen: true }, /* @__PURE__ */ React28.createElement(Dialog2.Content, { "aria-label": ariaLabel }, /* @__PURE__ */ React28.createElement(React28.Suspense, { fallback: /* @__PURE__ */ React28.createElement(Spinner8, { className: "w-full p-4" }) }, /* @__PURE__ */ React28.createElement(ErrorBoundary, null, output))));
};

// src/routing/AuthRoutes.tsx
var processChild = (child) => {
  if (child.type === AuthorisedRoute || child.type === AuthRoute) {
    const { element, children, ...rest } = child.props;
    return /* @__PURE__ */ React29.createElement(Route, { key: child.props.path, ...rest, element: /* @__PURE__ */ React29.createElement(AuthRoute, { ...rest }, element || children) });
  }
  if (child.type === AuthorisedDialogRoute || child.type === AuthDialogRoute) {
    const { element, children, ...rest } = child.props;
    return /* @__PURE__ */ React29.createElement(
      Route,
      {
        key: child.props.path,
        ...rest,
        element: /* @__PURE__ */ React29.createElement(AuthDialogRoute, { ...rest }, element || children)
      }
    );
  }
  if (child.type === React29.Fragment) {
    return React29.Children.toArray(child.props.children).filter((child2) => !!child2).map(processChild);
  }
  return child;
};
var AuthRoutes = ({ children }) => {
  return /* @__PURE__ */ React29.createElement(Routes, null, React29.Children.toArray(children).filter((child) => !!child).map(processChild));
};

// src/state/useLocalStorage.ts
import React30 from "react";
var useLocalStorage = (key, initialValue, raw) => {
  const [state, setState] = React30.useState(() => {
    try {
      const localStorageValue = localStorage.getItem(key);
      if (typeof localStorageValue !== "string") {
        localStorage.setItem(key, raw ? String(initialValue) : JSON.stringify(initialValue));
        return initialValue;
      }
      return raw ? localStorageValue : JSON.parse(localStorageValue || "null");
    } catch {
      return initialValue;
    }
  });
  React30.useEffect(() => {
    try {
      const serializedState = raw ? String(state) : JSON.stringify(state);
      localStorage.setItem(key, serializedState);
    } catch {
    }
  }, [key, raw, state]);
  const clear = () => localStorage.removeItem(key);
  return [state, setState, clear];
};

// src/types/Agreement.ts
var Plan = /* @__PURE__ */ ((Plan2) => {
  Plan2[Plan2["Internal"] = 0] = "Internal";
  Plan2[Plan2["StartUp"] = 1] = "StartUp";
  Plan2[Plan2["SmallBusiness"] = 2] = "SmallBusiness";
  Plan2[Plan2["Pro"] = 3] = "Pro";
  Plan2[Plan2["PartnerBundle"] = 4] = "PartnerBundle";
  Plan2[Plan2["ExternalArchive"] = 5] = "ExternalArchive";
  Plan2[Plan2["CurrentNonDK"] = 6] = "CurrentNonDK";
  Plan2[Plan2["Basis"] = 7] = "Basis";
  Plan2[Plan2["Plus"] = 8] = "Plus";
  Plan2[Plan2["Smart"] = 9] = "Smart";
  Plan2[Plan2["Advanced"] = 10] = "Advanced";
  Plan2[Plan2["Legacy"] = 11] = "Legacy";
  Plan2[Plan2["Unknown"] = 12] = "Unknown";
  return Plan2;
})(Plan || {});

// src/featureFlags/createFeatureFlags.tsx
import { useMemo, useState as useState2 } from "react";

// src/featureFlags/useDevFlags.tsx
import { useEffect, useState } from "react";
var PREFIX = "feature:";
function stripPrefix(key) {
  return key.split(PREFIX)[1];
}
function useDevFlags() {
  const [devFeatures, setDevFeatures] = useState(() => {
    if (process.env.NODE_ENV === "development") {
      return Object.entries(localStorage).reduce((flags, [key, value]) => {
        if (!key.startsWith(PREFIX)) {
          return flags;
        }
        return {
          ...flags,
          [stripPrefix(key)]: value
        };
      }, {});
    }
    return {};
  });
  if (process.env.NODE_ENV === "development") {
    useEffect(() => {
      function handleStorageChange({ key, newValue }) {
        if (key === null) {
          setDevFeatures({});
          return;
        }
        if (!key.startsWith(PREFIX)) {
          return;
        }
        if (newValue === null) {
          setDevFeatures((prev) => {
            const next = { ...prev };
            delete next[stripPrefix(key)];
            return next;
          });
          return;
        }
        setDevFeatures((prev) => ({
          ...prev,
          [stripPrefix(key)]: newValue
        }));
      }
      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    }, []);
  }
  return devFeatures;
}

// src/featureFlags/createFeatureFlags.tsx
function createFeatureFlags(config) {
  function useFeatureFlags() {
    const { features } = useCurrentAgreement();
    const { onFlagExposed } = useHostContext();
    const [trackedFlags] = useState2(() => /* @__PURE__ */ new Set());
    const devFeatures = useDevFlags();
    return useMemo(
      () => ({
        isEnabled(feature, options) {
          const devToggle = devFeatures[feature];
          let variant = features[feature] === true;
          if (devToggle !== void 0) {
            variant = devToggle === "true" || devToggle === "1";
          }
          if (options?.track && !trackedFlags.has(feature)) {
            trackedFlags.add(feature);
            onFlagExposed?.(feature, variant);
          }
          return variant;
        }
      }),
      [devFeatures, features, onFlagExposed, trackedFlags]
    );
  }
  return {
    useFeatureFlags,
    flags: config.flags
  };
}
export {
  Application,
  ApplicationContext,
  AuthDialogRoute,
  AuthRoute,
  AuthRoutes,
  Authorise,
  AuthorisedDialogRoute,
  AuthorisedLink,
  AuthorisedNavigate,
  AuthorisedRoute,
  CriticalError,
  ErrorBoundary,
  Host,
  HostContext,
  Layout,
  LazyRemote,
  LegacyFrame,
  LegacyFrameNavigator,
  NotFound,
  Plan,
  UnexpectedError,
  __internalState,
  addDatadogFeatureFlagEvaluation,
  analytics_default as analytics,
  cleanDatadogEvent,
  createFeatureFlags,
  datadogInit,
  getDetectedLocale,
  i18nResource_default as i18n,
  isDanishLocale,
  log_default as log,
  matchRoute,
  redirectToLegacyPage,
  refreshCurrentLegacyFrame,
  refreshCurrentLegacyPage,
  setDatadogUser,
  storeLocale,
  useAbsolutePath,
  useApplication,
  useApplicationContext,
  useCurrentAgreement,
  useCurrentLocale,
  useCurrentUser,
  useEnvironment,
  useHost,
  useHostContext,
  useHostRefetch,
  useLegacyFrame,
  useLocalStorage,
  usePathAuthorisation,
  useRouteAuthorisation
};
//# sourceMappingURL=index.esm.js.map